import { useCallback, useState } from 'react';

import { wallsApi } from '@walls/services/wallsApi';
import { useWallStore, type WallBoard } from '@walls/store';
import { useCardCommands } from './useCardCommands';

interface BoardPoint {
  x: number;
  y: number;
}

interface UploadImageCardsOptions {
  color: string;
  files: File[];
  point?: BoardPoint | null;
  title?: string;
}

export function useImageCardUpload(board: WallBoard) {
  const [isUploading, setIsUploading] = useState(false);
  const cardCommands = useCardCommands(board);
  const topicKey = useWallStore((state) => state.topicKey);

  const uploadImageCards = useCallback(
    async ({ color, files, point, title = '' }: UploadImageCardsOptions) => {
      if (files.length === 0 || isUploading) {
        return [];
      }

      setIsUploading(true);

      try {
        const normalizedTitle = title.trim();
        const uploadedCards = await wallsApi.uploadImageCards(
          board.id,
          files,
          normalizedTitle,
          color,
          topicKey
        );
        const cards = uploadedCards.map((card) => ({
          ...card,
          color,
          title: normalizedTitle,
        }));

        return point ? cardCommands.createCardsAt(cards, point) : cardCommands.createCards(cards);
      } finally {
        setIsUploading(false);
      }
    },
    [board.id, cardCommands, isUploading, topicKey]
  );

  return { isUploading, uploadImageCards };
}
